import { deleteDoc, doc } from 'firebase/firestore';
import qz from 'qz-tray';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '~/hooks/auth';
import { useCashFlow } from '~/hooks/cashFlow';
import { useToast } from '~/hooks/toast';
import ICashFlow from '~/interfaces/ICashFlow';
import IOrder from '~/interfaces/IOrder';
import { DB } from '~/services/firebase';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import { getPrintCashFlowEscPos } from '~/utils/getPrintCashFlowEscPos';
import { getPrintCashFlowHTML } from '~/utils/getPrintCashFlowHTML';
import { processCashFlow } from '~/utils/processCashFlow';

interface CashFlowProps {
  initDate: string;
  endDate: string;
  initHour: string;
  endHour: string;
  moneyValue: number;
  orders: IOrder[];
}

export function CashFlow({
  initDate,
  endDate,
  initHour,
  endHour,
  moneyValue,
  orders,
}: CashFlowProps): JSX.Element {
  const { company } = useAuth();
  const { addToast } = useToast();
  const { loadLaunchs, launchs } = useCashFlow();

  const valueInCash = useMemo(() => {
    let total = moneyValue;

    launchs.forEach(item => {
      if (item.type === '0') total -= item.value;
      if (item.type === '1' || item.type === '100')
        total += parseFloat(`${item.value}`);
    });

    return total;
  }, [launchs, moneyValue]);

  useEffect(() => {
    if (company.uid) {
      loadLaunchs({
        initDate,
        initHour,
        endDate,
        endHour,
        company_id: company.uid,
      });
    }
  }, [company, loadLaunchs, initDate, initHour, endDate, endHour]);

  const handleConfirmRemove = useCallback(
    async (item: ICashFlow) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do lançamento?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        await deleteDoc(doc(DB, 'cashflow', item.uid));

        loadLaunchs({
          initDate,
          initHour,
          endDate,
          endHour,
          company_id: company.uid,
        });

        addToast({
          type: 'success',
          description: 'Lançamento foi removido com sucesso.',
        });
      }
    },
    [addToast, company, loadLaunchs, initDate, initHour, endDate, endHour],
  );

  async function handlePrint() {
    try {
      const savedPrinters = JSON.parse(
        localStorage.getItem('@tempedido:printers') || '{}',
      );

      if (
        (window as any).electronAPI &&
        (window as any).electronAPI.printText
      ) {
        if (Object.keys(savedPrinters).length > 0) {
          const data = processCashFlow({ company, launchs, orders });

          (window as any).electronAPI
            .printText(data)
            .then((response: any) => console.log(response));

          return;
        }
      }

      if (company.escpos_printer) {
        const command = getPrintCashFlowEscPos({
          company,
          launchs,
          orders,
        });

        console.log(command);

        try {
          await qz.websocket.connect();
        } catch (error) {
          console.log(error);
        } finally {
          const config = qz.configs.create(company.escpos_printer);
          await qz.print(config, [command]);
        }

        return;
      }

      const content = getPrintCashFlowHTML({
        company,
        launchs,
        orders,
      });

      const win = window.open('about:blank');

      if (win) {
        win.document.write(content);
        win.window.print();
        win.window.close();
      }
    } catch (error: any) {
      console.log(error);
      addToast({
        type: 'danger',
        description: error.message,
      });
    }
  }

  return (
    <div className="col-xl-8 offset-xl-2 mt-4">
      <div className="nk-block-head nk-block-head-sm">
        <div className="nk-block-between">
          <div className="nk-block-head-content w-100 d-flex justify-content-between align-items-center">
            <h6 className="nk-block-title mb-0">
              {'Fluxo de Caixa '}
              <span className="">
                {`(Valor em Caixa: ${getCurrencyFormatted(valueInCash)})`}
              </span>
            </h6>

            <div>
              <button
                type="button"
                className="btn btn-sm btn-primary mr-2"
                onClick={handlePrint}
              >
                <em className="icon ni ni-printer" />
                <span>Imprimir</span>
              </button>

              <Link to="/cashflow/create" className="btn btn-sm btn-primary">
                <em className="icon ni ni-plus" />
                <span>Adicionar</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="nk-block">
        <div className="card card-bordered">
          <div className="card-inner-group">
            <div className="card-inner p-0">
              <div className="nk-tb-list">
                <div className="nk-tb-item nk-tb-head">
                  <div className="nk-tb-col">
                    <span>Descrição</span>
                  </div>
                  <div className="nk-tb-col">
                    <span>Tipo</span>
                  </div>
                  <div className="nk-tb-col">
                    <span>Valor</span>
                  </div>
                  <div className="nk-tb-col" />
                </div>

                {launchs.map(item => (
                  <div className="nk-tb-item" key={item.uid}>
                    <div className="nk-tb-col">
                      <span className="tb-product">{item.name}</span>
                    </div>
                    <div className="nk-tb-col">
                      <span className="tb-product">
                        {item.type === '0' && 'Saída'}
                        {item.type === '1' && 'Entrada'}
                        {item.type === '100' && 'Abertura de Caixa'}
                        {item.type === '99' && 'Fechamento de Caixa'}
                      </span>
                    </div>
                    <div className="nk-tb-col">
                      <span className="tb-product">{item.value_formatted}</span>
                    </div>
                    <div className="nk-tb-col d-flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleConfirmRemove(item)}
                        className="mr-2"
                      >
                        <em className="icon ni ni-trash-fill me-1" />
                        Excluir
                      </button>
                      <Link
                        to={{
                          pathname: `/cashflow/edit/${item.uid}`,
                          state: item,
                        }}
                      >
                        <button type="button">
                          <em className="icon ni ni-edit-fill" />
                          <span>Editar</span>
                        </button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
